import {
    type ActionCodeSettings,
    type AdditionalUserInfo,
    type AuthProvider,
    applyActionCode,
    checkActionCode,
    confirmPasswordReset,
    deleteUser,
    EmailAuthProvider,
    GithubAuthProvider,
    GoogleAuthProvider,
    getAdditionalUserInfo,
    getAuth,
    getIdToken,
    getMultiFactorResolver,
    getRedirectResult,
    linkWithPopup,
    type MultiFactorError,
    multiFactor,
    type OAuthCredential,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithEmailLink,
    signInWithPopup,
    signInWithRedirect,
    signOut,
    TotpMultiFactorGenerator,
    type TotpSecret,
    type User,
    type UserCredential,
    unlink,
    updateCurrentUser,
    updatePassword,
    verifyBeforeUpdateEmail,
    verifyPasswordResetCode,
} from 'firebase/auth';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { useHiveClient } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ToastOptions, toast } from '@gitbook/spine-react';
import { reportException, sanitizeError, Trace } from '@gitbook/util-debug';
import { localStorage } from '@gitbook/util-local-storage';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { STORAGE_METHOD_KEY } from '../../Screens/Login/LoginScreen';
import { apiClient, apiQueryPersister } from '../api';
import { useContactSupport } from '../support';

export interface FirebaseMFAEnrollmentSecret {
    totpUri: string;
    totpSecret: TotpSecret;
}

export interface FirebaseMFARequiredState {
    error: MultiFactorError;
    reset: () => void;
}

/**
 * Normal errors that should not be captured by Sentry and will not be thrown to the caller.
 *
 * https://firebase.google.com/docs/reference/js/firebase.auth.Auth#signInWithEmailAndPassword
 * https://firebase.google.com/docs/reference/js/firebase.auth.Auth#createUserWithEmailAndPassword
 * https://firebase.google.com/docs/reference/js/firebase.auth.Error
 * https://github.com/firebase/firebase-js-sdk/blob/master/packages/auth/src/core/errors.ts
 */
const NORMAL_ERRORS = [
    'auth/network-request-failed',
    'auth/too-many-requests',
    'auth/user-disabled',
    'auth/popup-closed-by-user',
    'auth/redirect-cancelled-by-user',
    'auth/cancelled-popup-request',
    'auth/user-cancelled',
    'auth/credential-already-in-use',
    // For sign-up
    'auth/email-already-in-use',
    'auth/weak-password',
    'auth/account-exists-with-different-credential',
    // For login
    'auth/user-mismatch',
    'auth/invalid-email',
    'auth/user-not-found',
    'auth/wrong-password',
    'auth/user-disabled',
    'auth/too-many-requests',
    // For apply of action code
    'auth/expired-action-code',
    'auth/invalid-action-code',
    // Phone authentication
    'auth/code-expired',
    'auth/invalid-phone-number',

    // Errors the should be handled by the caller, and should not be included in this list:
    // 'auth/invalid-verification-code',
];

/**
 * Errors to ignore and not show to the user,
 * but will still be logged in Sentry if not listed as well in NORMAL_ERRORS.
 */
const IGNORE_ERRORS = [
    'auth/redirect-cancelled-by-user',
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/user-cancelled',
    'auth/invalid-verification-code',
];

type ToastableErrorDetails = Partial<ToastOptions> & {
    title?: string;
    withContactSupport?: boolean;
};

/**
 * Better message for errors than the default one from Firebase.
 */
const PUBLIC_ERRORS: { [key: string]: ToastableErrorDetails } = {
    'auth/code-expired': {
        description: 'The SMS code has expired. Try again with a new code.',
        withContactSupport: false,
    },
    'auth/invalid-phone-number': {
        description: 'The format of the phone number provided is incorrect.',
        withContactSupport: false,
    },
    'auth/network-request-failed': {
        description:
            'A network error has occurred. The service may be blocked by your ISP or in your area.',
        withContactSupport: false,
    },
    // We don't want to publicly display that the username is valid
    'auth/wrong-password': {
        description: 'Invalid email address or password.',
        withContactSupport: false,
    },
    'auth/invalid-credential': {
        title: 'Invalid email address or password.',
        withContactSupport: false,
    },
    'auth/account-exists-with-different-credential': {
        title: 'Account already exists',
        description:
            'An account already exists with the same email address but different ' +
            'sign-in credentials. Sign in using a provider associated with this ' +
            'email address.',
        withContactSupport: false,
    },
    'auth/user-mismatch': {
        title: 'Authentication mismatch',
        description:
            'The authentication used does not match the one used to sign in. Authenticate with he correct account.',
        withContactSupport: false,
    },
    'auth/credential-already-in-use': {
        title: 'Potential duplicated accounts',
        description:
            'This credential is already associated with a different user account. ' +
            'Sign out and sign in using those credentials.',
        withContactSupport: false,
    },
    'auth/email-already-in-use': {
        title: 'Email already in use',
        description:
            'This email is already associated with a different user account. ' +
            'Sign out and sign in using those credentials.',
        withContactSupport: false,
    },
    'auth/invalid-action-code': {
        title: 'Invalid or expired link',
        description:
            'The authentication link was expired, malformed or has already been used. If it happens often, contact our support for assistance.',
        withContactSupport: true,
    },
};

/**
 * Some errors are not directly returned by Firebase, but are wrapped in the error message inlined.
 * This function extracts the error message and returns the error details.
 */
function extractInlineAuthError(error): ToastableErrorDetails | null {
    if (error.code === 'auth/internal-error') {
        try {
            const inlineError = JSON.parse(error.message.match(/{.*}/)[0]);

            // TODO Couldn't manage to pass some extra `expose` property to the error object
            // so relying on message content to know if we should expose the error or not
            if (inlineError?.error?.message) {
                return {
                    description: inlineError.error.message,
                    withContactSupport: true,
                };
            }
        } catch {
            return null;
        }
    }

    return null;
}

/**
 * Key used to store the redirect state in localStorage.
 */
const REDIRECT_LOCAL_STORAGE_KEY = 'auth-redirect-state';

/**
 * Descriptor for where to redirect the user after a successful sign in.
 */
interface PostAuthURL {
    pathname?: string;
    prefix?: string;
}

/**
 * Return functions to execute auth operations (reset password, etc).
 * Compared to the native functions, it handles errors and show a toast to the user.
 */
export function useAuthOperations(
    props: {
        /**
         * Callback when an operation require a multi-factor auth.
         * If not provider, the returned value `mfa` will be set.
         */
        onMultiFactor?: (state: FirebaseMFARequiredState) => void;
        /**
         * Callback when an operation require a recent login.
         */
        onRequireRecentLogin?: () => void;
    } = {}
) {
    const contactSupport = useContactSupport();
    const hiveClient = useHiveClient();

    const [mfa, setMFAState] = React.useState<FirebaseMFARequiredState | null>(null);
    const latestErrorNotificationRef = React.useRef<null | (() => void)>(null);
    const currentAuthMethodRef = React.useRef<null | analytics.AnalyticsAuthenticationMethod>(null);

    const onMultiFactor = useEventCallback(
        props.onMultiFactor ??
            ((state) => {
                setMFAState(state);
            })
    );

    const onRequireRecentLogin = useEventCallback(
        props.onRequireRecentLogin ??
            (() => {
                throw new Error(
                    'Recent login required, but "onRequireRecentLogin" is not implemented.'
                );
            })
    );

    const handleException = React.useCallback(
        (error: Error & { code?: string }) => {
            if (error.code === 'auth/multi-factor-auth-required') {
                onMultiFactor({
                    error: error as MultiFactorError,
                    reset: () => {
                        setMFAState(null);
                    },
                });
                return;
            }

            if (error.code === 'auth/requires-recent-login') {
                onRequireRecentLogin();
                return;
            }

            // Display only one auth error at a time, and close the existing one if any.
            if (latestErrorNotificationRef.current) {
                latestErrorNotificationRef.current();
            }

            if (!('code' in error) || typeof error.code !== 'string') {
                const id = toast.error('An unexpected error occurred', error, { critical: true });
                latestErrorNotificationRef.current = () => toast.dismiss(id);

                throw error;
            }

            const shouldBeIgnored = IGNORE_ERRORS.includes(error.code);

            const isNormal = NORMAL_ERRORS.includes(error.code);

            if (!shouldBeIgnored) {
                const { withContactSupport = true, ...toastSpec } = (PUBLIC_ERRORS[error.code] ||
                    extractInlineAuthError(error)) ?? {
                    details: isNormal ? error.message : sanitizeError(error),
                };

                const id = toast.error(toastSpec.title ?? 'An unexpected error occurred', null, {
                    ...toastSpec,
                    action: withContactSupport ? (
                        <Button
                            label="Contact support"
                            onPress={() => {
                                contactSupport({
                                    message: `\n\n---\nError: ${error.code}\n${error.message}\n`,
                                });
                            }}
                        />
                    ) : undefined,
                    critical: true,
                });
                latestErrorNotificationRef.current = () => toast.dismiss(id);
            }

            if (!isNormal) {
                // Track error in Sentry
                throw error;
            }
        },
        [contactSupport, onMultiFactor, onRequireRecentLogin]
    );

    const wrapException = useEventCallback(
        async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
            const attempt = async (retried = false): Promise<T | undefined> => {
                try {
                    return await fn();
                } catch (error) {
                    // We retry if the backend took too long to respond
                    // because it means there was a cold start in the cloud function.
                    if (error.message.toLowerCase().includes('deadline exceeded') && !retried) {
                        reportException(error);

                        return attempt(true);
                    }

                    handleException(error);
                }
            };

            return attempt();
        }
    );

    return {
        mfa,

        /**
         * Sign out the current user.
         */
        signOut: React.useCallback(async () => {
            const trace = new Trace('auth.useSignOut');
            return trace.wrap(async () => {
                analytics.logEvent({
                    type: 'auth_sign_out',
                });

                await Promise.all([apiQueryPersister.removeClient(), hiveClient.clearAuthCache()]);
                await signOut(getAuth());

                // Preserve some local storage variables we don't want to lose
                const previouslyUsedSigningMethod = localStorage.get(STORAGE_METHOD_KEY);

                // Clear localstorage to avoid leaking infos
                localStorage.clear();

                // Restore local storage variables we didn't want to lose
                if (previouslyUsedSigningMethod !== null) {
                    await localStorage.set(STORAGE_METHOD_KEY, previouslyUsedSigningMethod);
                }

                // Hard-reload the page to avoid side-effects and start from scratch
                window.location.href = '/';
            });
        }, [hiveClient]),

        /**
         * Send a sign-in link to an email.
         */
        sendSignInLinkToEmail: React.useCallback(
            (email: string, to?: PostAuthURL) => {
                const trace = new Trace('auth.sendSignInLinkToEmail');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        await apiClient.internal.signInWithEmailLink({
                            email,
                            url: createPostAuthURL(to),
                        });

                        analytics.logEvent({
                            type: 'auth_send_link',
                        });

                        return true;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Sign in with a link sent to an email address.
         */
        signInWithEmailLink: React.useCallback(
            (email: string, emailLink: string) => {
                const trace = new Trace('auth.signInWithEmailLink', {
                    sampleRate: 1,
                });
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        const { currentUser: previousUser } = auth;

                        currentAuthMethodRef.current = 'link';
                        const credentials = await signInWithEmailLink(auth, email, emailLink);
                        if (credentials.user) {
                            await apiClient.users.syncProfileWithAuth();
                            logAuthEvent(
                                previousUser,
                                credentials.user,
                                getAdditionalUserInfo(credentials),
                                'link'
                            );
                        }

                        return true as const;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Sign in with Google.
         */
        signInWithGoogle: React.useCallback(
            (email?: string): Promise<true | undefined> => {
                const trace = new Trace('auth.signInWithGoogle', {
                    sampleRate: 0.1,
                });
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const { currentUser: previousUser } = getAuth();

                        const provider = new GoogleAuthProvider();
                        if (email) {
                            provider.setCustomParameters({
                                login_hint: email,
                            });
                        }

                        currentAuthMethodRef.current = 'google';

                        const credentials = await authenticateWithProvider(provider);
                        if (credentials.user) {
                            await apiClient.users.syncProfileWithAuth();
                            logAuthEvent(
                                previousUser,
                                credentials.user,
                                getAdditionalUserInfo(credentials),
                                'google'
                            );
                        }

                        return true as const;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Sign in with GitHub (used for legacy users with already associated GitHub accounts).
         */
        signInWithGitHub: React.useCallback(
            (email?: string): Promise<true | undefined> => {
                const trace = new Trace('auth.signInWithGitHub', {
                    sampleRate: 1,
                });
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const { currentUser: previousUser } = getAuth();

                        const provider = new GithubAuthProvider();
                        if (email) {
                            provider.setCustomParameters({
                                login_hint: email,
                            });
                        }

                        currentAuthMethodRef.current = 'github';

                        const credentials = await authenticateWithProvider(provider);
                        if (credentials.user) {
                            await apiClient.users.syncProfileWithAuth();
                            logAuthEvent(
                                previousUser,
                                credentials.user,
                                getAdditionalUserInfo(credentials),
                                'github'
                            );
                        }

                        return true as const;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Redirect to the GitHub sign-in page.
         */
        redirectToGitHubAuth: React.useCallback(async () => {
            const trace = new Trace('auth.redirectToGitHubAuth', {
                sampleRate: 1,
            });
            return trace.wrap(async () => {
                return wrapException(async () => {
                    const provider = new GithubAuthProvider();
                    await redirectToAuthentication(provider, 'github-redirect');
                });
            });
        }, [wrapException]),

        /**
         * Handle the result of an authentication redirect.
         * The user is already automatically authenticated by Firebase when the redirect is done,
         * but this function is used to track the action in our analytics.
         */
        signInWithRedirectResult: React.useCallback(async () => {
            const trace = new Trace('auth.signInWithRedirectResult', {
                sampleRate: 1,
            });
            return trace.wrap(async () => {
                return wrapException(async () => {
                    currentAuthMethodRef.current = (localStorage.get(REDIRECT_LOCAL_STORAGE_KEY) ??
                        null) as analytics.AnalyticsAuthenticationMethod | null;
                    localStorage.remove(REDIRECT_LOCAL_STORAGE_KEY);

                    const credentials = await getRedirectResult(getAuth());
                    if (!credentials) {
                        return null;
                    }

                    const authMethod = getAnalyticsAuthenticationMethod(credentials?.providerId);

                    await apiClient.users.syncProfileWithAuth();
                    logAuthEvent(
                        null,
                        credentials.user,
                        getAdditionalUserInfo(credentials),
                        authMethod
                    );

                    return true as const;
                });
            });
        }, [wrapException]),

        /**
         * Sign in with email and password.
         */
        signInWithEmailAndPassword: React.useCallback(
            (email: string, password: string): Promise<true | undefined> => {
                const trace = new Trace('auth.signInWithEmailAndPassword', {
                    sampleRate: 1,
                });
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        const { currentUser: previousUser } = auth;

                        currentAuthMethodRef.current = 'password';

                        const credentials = previousUser
                            ? await reauthenticateWithCredential(
                                  previousUser,
                                  EmailAuthProvider.credential(email, password)
                              )
                            : await signInWithEmailAndPassword(auth, email, password);
                        if (credentials.user) {
                            await apiClient.users.syncProfileWithAuth();
                            logAuthEvent(
                                previousUser,
                                credentials.user,
                                getAdditionalUserInfo(credentials),
                                'password'
                            );
                        }

                        return true as const;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Prompt to link a Google account to the current user.
         */
        linkWithGoogle: React.useCallback(async () => {
            return wrapException(() => authenticateWithProvider(new GoogleAuthProvider()));
        }, [wrapException]),

        /**
         * Prompt to link a GitHub account to the current user.
         */
        linkWithGitHub: React.useCallback(async () => {
            return wrapException(() => authenticateWithProvider(new GithubAuthProvider()));
        }, [wrapException]),

        /**
         * Unlink the current user from Google.
         */
        unlinkFromGoogle: React.useCallback(async () => {
            return wrapException(() => unlinkFromProvider('google.com'));
        }, [wrapException]),

        /**
         * Unlink the current user from GitHub.
         */
        unlinkFromGitHub: React.useCallback(async () => {
            return wrapException(() => unlinkFromProvider('github.com'));
        }, [wrapException]),

        /**
         * Send an email verification to the current user.
         */
        sendEmailVerification: React.useCallback(
            async (
                actionCodeSettings: ActionCodeSettings = {
                    url: window.location.href,
                }
            ) => {
                const auth = getAuth();
                const { currentUser } = auth;
                assertIsDefined(currentUser);

                await wrapException(() => sendEmailVerification(currentUser, actionCodeSettings));

                toast('Check your emails', {
                    icon: Icon.Email,
                    description: `An email with a verification has link been sent. Follow the link to verify your (${currentUser.email}) email address.`,
                });

                analytics.logEvent({
                    type: 'auth_send_email_verification',
                });
            },
            [wrapException]
        ),

        /**
         * Delete current user.
         */
        deleteAccount: React.useCallback(() => {
            const trace = new Trace('auth.deleteCurrentUserAccount');
            return trace.wrap(async () => {
                return wrapException(async () => {
                    const auth = getAuth();
                    const { currentUser } = auth;
                    assertIsDefined(currentUser);
                    await deleteUser(currentUser);

                    analytics.logEvent({
                        type: 'account_delete',
                    });

                    return true;
                });
            });
        }, [wrapException]),

        /**
         * Send a password forgot link to an email.
         *
         * Returns `true` if email was sent, else `false`.
         */
        resetPassword: React.useCallback(
            (email: string, to?: PostAuthURL): Promise<boolean | undefined> => {
                const trace = new Trace('auth.resetPassword');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const { data } = await apiClient.internal.signInMethodsForEmail(email);

                        // If there's an SSO link for the related user, and
                        // there are no alternative sign in methods, we don't
                        // allow sending a password reset link.
                        if (data.methods.length === 0 && data.ssoOrganization) {
                            toast.error('Password reset not allowed', null, {
                                description: 'Sign in with SSO is required.',
                                critical: true,
                            });
                            return false;
                        }

                        const auth = getAuth();
                        const actionCodeSettings: ActionCodeSettings = {
                            url: createPostAuthURL(to),
                            handleCodeInApp: true,
                        };

                        await sendPasswordResetEmail(auth, email, actionCodeSettings);

                        analytics.logEvent({
                            type: 'auth_forgot_password',
                        });

                        return true;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Verify reset password code provided in mail (oobCode).
         */
        verifyPasswordResetCode: React.useCallback(
            (actionCode: string) => {
                const trace = new Trace('auth.verifyPasswordResetCode');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        const email = await verifyPasswordResetCode(auth, actionCode);
                        return email;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Confirm password reset and change the user's password. ActionCode is a parameter provided in mail (oobCode).
         */
        confirmPasswordReset: React.useCallback(
            (actionCode: string, newPassword: string) => {
                const trace = new Trace('auth.confirmPasswordReset');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        await confirmPasswordReset(auth, actionCode, newPassword);

                        analytics.logEvent({
                            type: 'auth_reset_password',
                        });
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Update or set the currently signed in user's password.
         */
        updatePassword: React.useCallback(
            (newPassword: string) => {
                const trace = new Trace('auth.updatePassword');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        const { currentUser } = auth;
                        if (!currentUser) {
                            throw new Error('No user is signed in.');
                        }
                        await updatePassword(currentUser, newPassword);

                        analytics.logEvent({
                            type: 'auth_update_password',
                        });

                        return true;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Update the currently signed in user's email and sync in database.
         */
        updateEmail: React.useCallback(
            (newEmail: string, actionCodeSettings?: ActionCodeSettings) => {
                const trace = new Trace('auth.updateEmail');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        const { currentUser } = auth;
                        assertIsDefined(currentUser);

                        await verifyBeforeUpdateEmail(currentUser, newEmail, actionCodeSettings);

                        analytics.logEvent({
                            type: 'auth_update_email',
                        });

                        return true;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Return the list of providers for the currently authenticated user.
         */
        fetchSignInMethodsForEmail: React.useCallback(
            (
                email: string
            ): Promise<api.OperationSpec<'signInMethodsForEmail'>['ResponseBody'] | undefined> => {
                const trace = new Trace('auth.fetchSignInMethodsForEmail');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const { data } = await apiClient.internal.signInMethodsForEmail(email);

                        analytics.logEvent({
                            type: 'auth_view_sign_in_methods',
                        });

                        return data;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Checks a verification code sent to the user by email or other out-of-band mechanism.
         */
        checkActionCode: React.useCallback(
            (oobCode: string) => {
                const trace = new Trace('auth.checkActionCode');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        return checkActionCode(auth, oobCode);
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Applies a verification code sent to the user by email or other out-of-band mechanism.
         */
        applyActionCode: React.useCallback(
            (oobCode: string) => {
                const trace = new Trace('auth.applyActionCode');
                return trace.wrap(async () => {
                    return wrapException(async () => {
                        const auth = getAuth();
                        await applyActionCode(auth, oobCode);
                        return true;
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Generate the secret to display to a user to enroll in multi-factor authentication.
         */
        generateMFAEnrollSecret: React.useCallback(async () => {
            return wrapException(async () => {
                const currentUser = getAuth().currentUser;
                assertIsDefined(currentUser);

                const userMfa = multiFactor(currentUser);
                if (userMfa.enrolledFactors.length > 0) {
                    return;
                }

                const multiFactorSession = await userMfa.getSession();
                const totpSecret =
                    await TotpMultiFactorGenerator.generateSecret(multiFactorSession);

                const totpUri = totpSecret.generateQrCodeUrl(
                    currentUser.email ?? currentUser.displayName ?? currentUser.uid,
                    'GitBook'
                );

                const result: FirebaseMFAEnrollmentSecret = {
                    totpUri,
                    totpSecret,
                };

                return result;
            });
        }, [wrapException]),

        /**
         * Enroll a user in multi-factor authentication.
         */
        enrollMFA: React.useCallback(
            async (secret: FirebaseMFAEnrollmentSecret, verificationCode: string) => {
                return wrapException(async () => {
                    const auth = getAuth();
                    const currentUser = auth.currentUser;
                    assertIsDefined(currentUser);

                    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
                        secret.totpSecret,
                        verificationCode
                    );
                    await multiFactor(currentUser).enroll(multiFactorAssertion, 'GitBook');

                    analytics.logEvent({
                        type: 'auth_mfa_enroll',
                    });
                });
            },
            [wrapException]
        ),

        /**
         * Unenroll a multi-factor authentication method.
         */
        unenrollMFA: React.useCallback(
            async (mfaEnrollmentId: string) => {
                return wrapException(async () => {
                    const auth = getAuth();

                    if (auth.currentUser) {
                        await multiFactor(auth.currentUser).unenroll(mfaEnrollmentId);
                        analytics.logEvent({
                            type: 'auth_mfa_unenroll',
                        });

                        // Ensure user is correctly propagated
                        await updateCurrentUser(auth, auth.currentUser);
                    }
                });
            },
            [wrapException]
        ),

        /**
         * Authenticate with a MFA code.
         */
        applyMFACode: React.useCallback(
            async (state: FirebaseMFARequiredState, code: string) => {
                return wrapException(async () => {
                    const auth = getAuth();
                    const { currentUser: previousUser } = auth;
                    const mfaResolver = getMultiFactorResolver(auth, state.error);

                    const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
                        mfaResolver.hints[0].uid,
                        code
                    );

                    await mfaResolver.resolveSignIn(multiFactorAssertion);
                    const { currentUser } = auth;

                    if (currentUser && currentAuthMethodRef.current) {
                        await apiClient.users.syncProfileWithAuth();
                        logAuthEvent(
                            previousUser,
                            currentUser,
                            null,
                            currentAuthMethodRef.current,
                            true
                        );
                    }

                    state.reset();
                });
            },
            [wrapException]
        ),
    };
}

/**
 * Create a url for the post-authentication step
 */
function createPostAuthURL(to: PostAuthURL = {}): string {
    const { protocol, host, pathname } = window.location;
    return new URL(
        `${to.prefix || ''}${to.pathname || pathname}`,
        `${protocol}//${host}`
    ).toString();
}

/**
 * Logs an auth event - either a sign in or a sign up depending on the action of the user.
 */
function logAuthEvent(
    previousUser: User | null,
    user: User,
    additionalUserInfo: AdditionalUserInfo | null | undefined,
    method: analytics.AnalyticsAuthenticationMethod,
    mfa?: boolean
): void {
    if (previousUser && previousUser.uid === user.uid) {
        analytics.logEvent({
            type: 'auth_reauthenticate',
            method,
            mfa,
        });
    } else {
        analytics.identify();

        if (additionalUserInfo?.isNewUser) {
            analytics.logEvent({
                type: 'auth_sign_up',
                email: user.email ?? undefined,
                method,
                withSSO: false,
            });
        } else {
            analytics.logEvent({
                type: 'auth_sign_in',
                method,
                mfa,
            });
        }
    }
}

/**
 * Get the AnalyticsAuthenticationMethod for a Firebase provider.
 */
function getAnalyticsAuthenticationMethod(
    providerId: string | undefined | null
): analytics.AnalyticsAuthenticationMethod {
    switch (providerId) {
        case 'google.com':
            return 'google';
        case 'github.com':
            return 'github';
        default:
            return 'link';
    }
}

/**
 * Test if the user is authenticating using a redirect.
 * This method is synchronous and use the localStorage to store the state.
 *
 * It returns a function to call when the authentication is done.
 */
export function useIsRedirectedForAuth(): null | (() => void) {
    const [done, setDone] = React.useState(false);

    return React.useMemo(() => {
        if (done) {
            return null;
        }

        if (localStorage.get(REDIRECT_LOCAL_STORAGE_KEY) === null) {
            return null;
        }

        return () => {
            setDone(true);
        };
    }, [done]);
}

/**
 * Authenticate a user using a redirect flow.
 */
async function redirectToAuthentication(
    provider: AuthProvider,
    method: analytics.AnalyticsAuthenticationMethod
): Promise<void> {
    localStorage.set(REDIRECT_LOCAL_STORAGE_KEY, method);
    await signInWithRedirect(getAuth(), provider);
}

/**
 * Authenticate a user with a auth provider.
 * If the user is not initially link to this provider, it links it.
 */
async function authenticateWithProvider(provider: AuthProvider): Promise<
    UserCredential & {
        credential?: OAuthCredential | null;
    }
> {
    const auth = getAuth();
    const { currentUser } = auth;

    if (!currentUser) {
        // No user logged-in, we sign-in with the provider
        return signInWithPopup(auth, provider);
    }

    const hasAlreadyProvider = currentUser.providerData.some(
        (data) => data && data.providerId === provider.providerId
    );

    const result = await (hasAlreadyProvider
        ? reauthenticateWithPopup(currentUser, provider)
        : linkWithPopup(currentUser, provider));

    if (!result.user) {
        return result;
    }

    // Ensure user is correctly propagated
    await updateCurrentUser(auth, result.user);

    // Force refresh of onIdTokenChanged
    await getIdToken(result.user, true);

    return result;
}

/**
 * Unlink a user from a provider.
 */
async function unlinkFromProvider(providerId: 'google.com' | 'github.com') {
    const auth = getAuth();
    const { currentUser } = auth;
    if (!currentUser) {
        return;
    }

    const result = await unlink(currentUser, providerId);
    await updateCurrentUser(auth, result);

    // Force refresh of onIdTokenChanged
    await result.getIdToken(true);

    return result;
}
